export async function requestPushPermission(): Promise<boolean> {
  if (!("Notification" in window)) {
    console.warn("This browser does not support notifications")
    return false
  }

  if (Notification.permission === "granted") {
    return true
  }

  if (Notification.permission === "denied") {
    console.warn("Notification permission was previously denied")
    return false
  }

  try {
    const permission = await Notification.requestPermission()
    return permission === "granted"
  } catch (error) {
    console.error("Error requesting notification permission:", error)
    return false
  }
}

async function waitForServiceWorkerRegistration(
  maxWaitTime = 5000,
): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    return null
  }

  const startTime = Date.now()

  while (Date.now() - startTime < maxWaitTime) {
    if (navigator.serviceWorker.controller) {
      try {
        return await navigator.serviceWorker.ready
      } catch (error) {
        console.error("Error getting service worker ready:", error)
        return null
      }
    }

    const registrations = await navigator.serviceWorker.getRegistrations()
    if (registrations.length > 0) {
      const registration = registrations[0]
      if (registration.active || registration.installing || registration.waiting) {
        try {
          return await navigator.serviceWorker.ready
        } catch (error) {
          console.error("Error getting service worker ready:", error)
          return null
        }
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  console.warn("Service worker registration timeout")
  return null
}

export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not supported in this browser")
  }

  if (!("PushManager" in window)) {
    throw new Error("Push Manager is not supported in this browser")
  }

  let registration: ServiceWorkerRegistration | null = null
  try {
    registration = await waitForServiceWorkerRegistration()
  } catch (error) {
    throw new Error(
      `Service worker not ready: ${error instanceof Error ? error.message : "Unknown error"}`,
    )
  }

  if (!registration) {
    throw new Error(
      "Service worker registration not available. Please refresh the page and try again.",
    )
  }

  if (!registration.pushManager) {
    throw new Error("Push Manager not available on service worker registration")
  }

  try {
    const key = urlBase64ToUint8Array(await getVapidPublicKey())
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key as BufferSource,
    })

    return subscription
  } catch (error) {
    console.error("Error subscribing to push notifications:", error)
    if (error instanceof Error) {
      console.error("Error details:", error.message, error.stack)
      throw new Error(`Failed to subscribe: ${error.message}`)
    }
    throw new Error("Failed to subscribe to push notifications: Unknown error")
  }
}

export async function unsubscribeFromPushNotifications(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) {
    return false
  }

  const registration = await navigator.serviceWorker.ready

  try {
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      await subscription.unsubscribe()
      return true
    }
    return false
  } catch (error) {
    console.error("Error unsubscribing from push notifications:", error)
    return false
  }
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) {
    return null
  }

  const registration = await navigator.serviceWorker.ready

  try {
    return await registration.pushManager.getSubscription()
  } catch (error) {
    console.error("Error getting push subscription:", error)
    return null
  }
}

async function getVapidPublicKey(): Promise<string> {
  const response = await fetch("/push_subscriptions/vapid_public_key")
  if (!response.ok) {
    let errorMessage = "Failed to get VAPID public key"
    try {
      const errorData = await response.json()
      if (errorData.error) {
        errorMessage = errorData.error
      } else if (response.status === 404) {
        errorMessage = "VAPID keys are not configured. Please contact support."
      } else if (response.status === 401 || response.status === 403) {
        errorMessage = "Authentication required. Please refresh the page and try again."
      }
    } catch {
      if (response.status === 404) {
        errorMessage = "VAPID keys are not configured. Please contact support."
      } else if (response.status === 401 || response.status === 403) {
        errorMessage = "Authentication required. Please refresh the page and try again."
      } else {
        errorMessage = `Failed to get VAPID public key (HTTP ${response.status})`
      }
    }
    throw new Error(errorMessage)
  }
  const data = await response.json()
  if (!data.public_key) {
    throw new Error("VAPID public key not found in server response")
  }
  return data.public_key
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export async function saveSubscriptionToServer(
  subscription: PushSubscription,
): Promise<boolean> {
  const key = subscription.getKey("p256dh")
  const auth = subscription.getKey("auth")

  if (!key || !auth) {
    return false
  }

  const response = await fetch("/push_subscriptions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCSRFToken(),
    },
    body: JSON.stringify({
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64(key),
        auth: arrayBufferToBase64(auth),
      },
    }),
  })

  return response.ok
}

export async function removeSubscriptionFromServer(
  endpoint: string,
): Promise<boolean> {
  const response = await fetch("/push_subscriptions", {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": getCSRFToken(),
    },
    body: JSON.stringify({ endpoint }),
  })

  return response.ok
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

function getCSRFToken(): string {
  const token = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content")
  return token || ""
}
